import { PageHeader } from "@/components/PageHeader";
import { useConfirm } from "@/contexts/ConfirmDialogContext";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/utils";
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@a7y/tailwind-catalyst";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Handshake, Search, X } from "lucide-react";
import { useMemo, useState, type ChangeEvent } from "react";
import { useNavigate } from "react-router-dom";
import { AffiliateStatusBadge, LoadingPanel } from "./shared";
import type { Affiliate, AffiliateStatus } from "./types";

type StatusFilter = "all" | AffiliateStatus;
const FILTERS: { value: StatusFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "pending", label: "Pending" },
  { value: "active", label: "Active" },
  { value: "disabled", label: "Disabled" },
  { value: "suspicious", label: "Suspicious" },
];

type StatusMessage = { type: "success" | "error"; text: string };

export function AffiliatesPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const confirm = useConfirm();

  const [filter, setFilter] = useState<StatusFilter>("all");
  const [search, setSearch] = useState("");
  const [statusMessage, setStatusMessage] = useState<StatusMessage | null>(null);

  const showStatus = (type: "success" | "error", text: string) => {
    setStatusMessage({ type, text });
    setTimeout(() => setStatusMessage(null), 4000);
  };

  // Fetch the full set once, then filter by tab + search client-side (index-free
  // per plan §6; the list is small and gives live per-tab counts).
  const { data, isLoading } = useQuery({
    queryKey: ["affiliates"],
    queryFn: () => api.affiliates.list(),
  });

  const affiliates: Affiliate[] = useMemo(() => data?.affiliates ?? [], [data]);

  const counts = useMemo(() => {
    const c: Record<StatusFilter, number> = {
      all: affiliates.length,
      pending: 0,
      active: 0,
      disabled: 0,
      suspicious: 0,
    };
    for (const a of affiliates) c[a.status] += 1;
    return c;
  }, [affiliates]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return affiliates.filter((a) => {
      if (filter !== "all" && a.status !== filter) return false;
      if (!term) return true;
      return (
        a.email.toLowerCase().includes(term) ||
        (a.name ?? "").toLowerCase().includes(term) ||
        a.id.toLowerCase().includes(term)
      );
    });
  }, [affiliates, filter, search]);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["affiliates"] });

  const approveMutation = useMutation({
    mutationFn: (id: string) => api.affiliates.approve(id),
    onSuccess: () => {
      invalidate();
      showStatus("success", "Affiliate approved");
    },
    onError: (err: Error) => showStatus("error", `Approve failed: ${err.message}`),
  });

  const disableMutation = useMutation({
    mutationFn: (id: string) => api.affiliates.disable(id),
    onSuccess: () => {
      invalidate();
      showStatus("success", "Affiliate disabled");
    },
    onError: (err: Error) => showStatus("error", `Disable failed: ${err.message}`),
  });

  const reenableMutation = useMutation({
    mutationFn: (id: string) => api.affiliates.reenable(id),
    onSuccess: () => {
      invalidate();
      showStatus("success", "Affiliate re-enabled");
    },
    onError: (err: Error) => showStatus("error", `Re-enable failed: ${err.message}`),
  });

  const handleApprove = async (a: Affiliate) => {
    const ok = await confirm({
      title: `Approve ${a.email}?`,
      description:
        "Approving promotes this pending affiliate to active and emails them their tracking links.",
      confirmLabel: "Approve",
      confirmColor: "green",
    });
    if (ok) approveMutation.mutate(a.id);
  };

  const handleDisable = async (a: Affiliate) => {
    const ok = await confirm({
      title: `Disable ${a.email}?`,
      description:
        "Disabling immediately stops attribution and portal access. Existing commissions and payouts are retained.",
      confirmLabel: "Disable",
      confirmColor: "red",
    });
    if (ok) disableMutation.mutate(a.id);
  };

  const handleReenable = async (a: Affiliate) => {
    const ok = await confirm({
      title: `Re-enable ${a.email}?`,
      description: "Re-enabling restores this disabled affiliate to active.",
      confirmLabel: "Re-enable",
      confirmColor: "amber",
    });
    if (ok) reenableMutation.mutate(a.id);
  };

  const anyPending =
    approveMutation.isPending || disableMutation.isPending || reenableMutation.isPending;

  return (
    <div className="px-4 py-0">
      <PageHeader title="Affiliates" subtitle="First-party affiliate program accounts" />

      {/* Status filter tabs */}
      <div className="mb-6 flex gap-0 border-b border-zinc-200 dark:border-zinc-700">
        {FILTERS.map((f) => (
          <button
            key={f.value}
            onClick={() => setFilter(f.value)}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              filter === f.value
                ? "border-b-2 border-blue-500 text-blue-600 dark:text-blue-400"
                : "text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300"
            }`}
          >
            {f.label}
            <span className="ml-1.5 text-xs text-zinc-400">{counts[f.value]}</span>
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="mb-6 flex items-center justify-between gap-4">
        <div className="relative w-[340px]">
          <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            autoComplete="off"
            placeholder="Search by email, name, or id..."
            value={search}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
            className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-10 pr-10 text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-zinc-600 dark:bg-zinc-800 dark:text-white"
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {statusMessage && (
        <div
          className={`mb-4 rounded-lg px-4 py-2.5 text-sm font-medium ${
            statusMessage.type === "success"
              ? "bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400"
              : "bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400"
          }`}
        >
          {statusMessage.text}
        </div>
      )}

      {isLoading ? (
        <LoadingPanel />
      ) : (
        <Table dense striped>
          <TableHead>
            <TableRow>
              <TableHeader>Affiliate</TableHeader>
              <TableHeader>Status</TableHeader>
              <TableHeader>Channel</TableHeader>
              <TableHeader>Discount</TableHeader>
              <TableHeader>Created</TableHeader>
              <TableHeader className="text-right">Actions</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="py-8 text-center">
                  <Handshake className="mx-auto h-12 w-12 text-zinc-400" />
                  <p className="mt-2 text-zinc-500">No affiliates match this view</p>
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((a) => (
                <TableRow
                  key={a.id}
                  className="cursor-pointer"
                  onClick={() => navigate(`/admin/affiliates/${a.id}`)}
                >
                  <TableCell>
                    <div className="font-medium text-zinc-900 dark:text-zinc-100">
                      {a.name || a.email}
                    </div>
                    {a.name && <div className="text-xs text-zinc-500">{a.email}</div>}
                  </TableCell>
                  <TableCell>
                    <AffiliateStatusBadge status={a.status} />
                  </TableCell>
                  <TableCell className="text-sm text-zinc-500">
                    {a.promotionChannel || "—"}
                  </TableCell>
                  <TableCell className="text-sm text-zinc-500">
                    {a.customerDiscountPercent != null ? `${a.customerDiscountPercent}%` : "—"}
                  </TableCell>
                  <TableCell className="text-sm text-zinc-500">{formatDate(a.createdAt)}</TableCell>
                  <TableCell className="text-right">
                    <div
                      className="flex justify-end gap-2"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {a.status === "pending" && (
                        <Button
                          color="green"
                          disabled={anyPending}
                          onClick={() => handleApprove(a)}
                        >
                          Approve
                        </Button>
                      )}
                      {a.status === "active" && (
                        <Button color="red" disabled={anyPending} onClick={() => handleDisable(a)}>
                          Disable
                        </Button>
                      )}
                      {a.status === "disabled" && (
                        <Button
                          color="amber"
                          disabled={anyPending}
                          onClick={() => handleReenable(a)}
                        >
                          Re-enable
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
